import Button from "@components/ui/button";
import {
  Card,
  CardAction,
  CardCloseButton,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import Icon from "@components/ui/icon";
import { ScrollArea } from "@components/ui/scroll-area";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { IconVariable } from "@tabler/icons-react";
import { openProDialog, openTeamDialog } from "@/shared";
import type { PlanTier } from "../../variable-picker/types";
import { type BlockedVariable, highestRequiredPlan } from "../plan-rules";
import { useEffect } from "react";
import { track } from "@/features/analytics";
import { VARIABLES_EVENTS } from "../../analytics/variables-analytics";

const ID = "upgrade-to-publish";

const PLAN_LABEL: Record<PlanTier, string> = {
  starter: "Starter",
  pro: "Pro",
  team: "Team",
};

// Same crown chips as the variable picker's locked rows.
const PLAN_CHIP: Record<PlanTier, string> = {
  starter: "tw-border-starter-border tw-bg-starter-base",
  pro: "tw-border-pro-border tw-bg-pro-base",
  team: "tw-border-team-border tw-bg-team-base",
};

export interface UpgradeToPublishDialogProps {
  blocked: BlockedVariable[];
  /** Analytics source forwarded to the upgrade dialog. */
  source: string;
}

/**
 * Shown when a publish / version create or update is refused because the
 * story uses variables above the account's plan. Same Card layout as the
 * archive/delete story and delete theme dialogs; hands off to the existing
 * Pro/Team upgrade dialog.
 */
export const UpgradeToPublishDialog = ({
  blocked,
  source,
}: UpgradeToPublishDialogProps) => {
  const dispatch = useAppDispatch();
  const plan = highestRequiredPlan(blocked);
  const trackAction = (action: "open" | "upgrade" | "go back") =>
    track(VARIABLES_EVENTS.UPGRADE_TO_PUBLISH_DIALOG, {
      action,
      "required plan": plan,
      "variables count": blocked.length,
      source,
    });
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per opening
  useEffect(() => trackAction("open"), []);
  const dismiss = () => dispatch(closeDialog(ID));
  const close = () => {
    trackAction("go back");
    dismiss();
  };

  const upgrade = () => {
    trackAction("upgrade");
    dismiss();
    if (plan === "team") openTeamDialog(source);
    else openProDialog(source);
  };

  return (
    <Card className="tw-gap-0">
      <CardHeader>
        <CardTitle>Upgrade to publish</CardTitle>
        <CardAction>
          <CardCloseButton aria-label="Close upgrade dialog" onClick={close} />
        </CardAction>
      </CardHeader>
      <CardContent>
        <p className="tw-mb-4 tw-mt-6 tw-font-sans tw-text-sm tw-text-blue-700">
          This story uses variables available on the{" "}
          <span className="tw-font-semibold tw-text-blue">
            {PLAN_LABEL[plan]}
          </span>{" "}
          plan. Upgrade to publish it, or remove them from the story.
        </p>
        <ScrollArea maxHeight={240}>
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-0.5 tw-p-0">
            {blocked.map((variable) => (
              <li
                key={variable.name}
                className="tw-flex tw-h-10 tw-items-center tw-gap-3 tw-rounded tw-bg-blue-50/60 tw-px-3 tw-font-sans tw-text-sm tw-text-blue"
              >
                <IconVariable
                  size={16}
                  stroke={1.5}
                  className="tw-shrink-0 !tw-fill-none tw-text-blue-500"
                />
                <span className="tw-min-w-0 tw-flex-1 tw-truncate">
                  {variable.name}
                </span>
                <span
                  title={`${PLAN_LABEL[variable.requiredPlan]} plan`}
                  className={cn(
                    "tw-flex tw-size-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid",
                    PLAN_CHIP[variable.requiredPlan],
                  )}
                >
                  <Icon
                    icon="ri-vip-crown-fill"
                    className="!tw-text-xs !tw-text-white"
                  />
                </span>
              </li>
            ))}
          </ul>
        </ScrollArea>
        <div className="tw-mb-8" />
      </CardContent>
      <CardFooter className="tw-justify-end tw-gap-3">
        <Button variant="secondary" onClick={close}>
          Go back
        </Button>
        <Button variant={plan === "team" ? "team" : "pro"} onClick={upgrade}>
          <Icon icon="ri-vip-crown-fill" className="tw-mr-1.5 !tw-text-xs" />
          Upgrade to {PLAN_LABEL[plan]}
        </Button>
      </CardFooter>
    </Card>
  );
};

UpgradeToPublishDialog.id = ID;
